import findLastIndex from 'lodash/findLastIndex';
import isEqual from 'lodash/isEqual';
import compact from 'lodash/compact';
import difference from 'lodash/difference';
import { Moment } from 'moment';
import flatten from 'lodash/flatten';

import * as PRESTATIONS from '../constants/prestations';
import { FEET_PERMANENT_NAIL_POLISH, HANDS_GEL_NAIL_POLISH, MANICURE, PEDICURE } from '../constants/prestations';
import { SPORTS_COACHING_AT_HOME_CATEGORIES } from '../constants/universes';
import {
  Address,
  Appointment,
  BasketContent,
  BasketContentItem,
  BasketContentItemWithPrices,
  BasketContentWithPrices,
  BasketPrices,
  CategoryReference,
  ChildcareAllowanceParams,
  ChildrenParams,
  CleaningPrestation,
  CleaningPrestationOption,
  GiftCard,
  PrestationReference,
  Promotion,
  Schedule,
  Subcategory,
  Universe,
  UniverseReference,
} from '../types/commons';
import { Booking, Potential, ProChoice, ProChoiceAPI } from '../types/customer';
import { BasketState } from '../ducks/common/basket';
import type { BasketState as CustomerBasketState } from '../ducks/customer/types';
import {
  ChildcareGeneratedPotential,
  CleaningGeneratedPotential,
  GeneratedPotential,
  RecurringChildcareGeneratedPotential,
} from '../types/basket';

import moment, { areMomentArraysEqual } from './moment';
import RULES from './prestations-rules';
import { getAllPrestationsForCategory, getPrestationsTitles } from './prestations';
import { parseTime } from './time';
import {
  getChildcareAllowanceLevels,
  isChildcare,
  isChildcareReference,
  isCleaningReference,
  isHourlyBasedReference,
  isSportsCoachingReference,
} from './universes';
import { isWanted } from './previous-pros';
import { formateDateForAPI } from './date';
import { shouldUseRecurrenceOrders } from './booking';

export const formatBasketForAPI = (basketContent: BasketContent) =>
  basketContent
    .filter((category) => category.prestations.length)
    .map((category, index) => category.prestations.map((prestation) => `${index}.${prestation}`))
    .join();

// apply the rules for all the prestations (the one we just selected included)
export const applyRulesToPrestations = (category: string, prestations: string[], addedPrestation?: string) => {
  const rules = RULES[category];
  if (!rules?.length) return prestations;

  return rules.reduce((newPrestations, rule) => rule(newPrestations, addedPrestation), prestations);
};

export const canIncludePrestation = (prestation: string, basketContentItem: BasketContentItem) =>
  applyRulesToPrestations(
    basketContentItem.reference,
    [...basketContentItem.prestations, prestation],
    prestation,
  ).includes(prestation);

export const shouldNotRemovePrestation = (prestation: string, basketContentItem: BasketContentItem) =>
  (prestation === MANICURE &&
    basketContentItem.prestations.includes(MANICURE) &&
    basketContentItem.prestations.includes(HANDS_GEL_NAIL_POLISH)) ||
  (prestation === PEDICURE &&
    basketContentItem.prestations.includes(PEDICURE) &&
    basketContentItem.prestations.includes(FEET_PERMANENT_NAIL_POLISH));

/**
 * Adds or removes a prestation in the basket depending on a few business rules.
 * @param index The index of the targeted basket category in basketContent.
 * @param reference The reference of the prestation to be added.
 * @param isAdding A boolean indicating whether the prestation is added or not.
 * @param basketContent The current basketContent
 */
export const addOrRemovePrestation = (
  index: number,
  reference: PrestationReference,
  isAdding: boolean,
  basketContent: BasketContent,
) => {
  const basketCategory = basketContent[index];
  const basketPrestations = basketCategory.prestations;

  const addedPrestation = isAdding ? reference : undefined;

  const newPrestations = isAdding
    ? basketPrestations.concat(reference)
    : basketPrestations.filter((p) => p !== reference);

  return applyRulesToPrestations(basketCategory.reference, newPrestations, addedPrestation);
};

export const updateBasketContentByIndex = (
  index: number,
  reference: string,
  checked: boolean,
  basketContent: BasketContent,
) => [
  ...basketContent.slice(0, index),
  {
    ...basketContent[index],
    prestations: addOrRemovePrestation(index, reference, checked, basketContent),
  },
  ...basketContent.slice(index + 1, basketContent.length),
];

export const countSubcategoryPrestations = (index: number, basketContent: BasketContent, subcategory: Subcategory) =>
  subcategory.prestations.filter((presta) =>
    basketContent[index].prestations.some((reference) => reference === presta.reference),
  ).length;

export const addCategory = (category: string, basketContent: BasketContent) => [
  ...basketContent,
  {
    reference: category,
    prestations: [],
  },
];

export const removeCategory = (category: string, basketContent: BasketContent) => {
  const lastIndex = findLastIndex(basketContent, (c) => c.reference === category);

  return lastIndex < 0
    ? basketContent
    : [...basketContent.slice(0, lastIndex), ...basketContent.slice(lastIndex + 1, basketContent.length)];
};

export const removeCategoriesFromBasket = (categories: CategoryReference[], basketContent: BasketContent) =>
  basketContent.filter((category) => !categories.includes(category.reference));

export const containsCategories = (categories: string[], basketContent: BasketContent) =>
  basketContent.some((category) => categories.includes(category.reference));

export const needsCovidProtocol = (universe: UniverseReference, basketContent: BasketContent) =>
  !isSportsCoachingReference(universe) || containsCategories(SPORTS_COACHING_AT_HOME_CATEGORIES, basketContent);

type UpdateAppointmentResult = {
  appointment?: Appointment;
  fieldsToUpdate: {} | { appointmentNear: boolean };
};

export const updateAppointment = (
  { primary, secondary }: Appointment,
  newAppointment: Moment,
  hasNearPro?: boolean,
  hasNearProToday?: boolean,
): UpdateAppointmentResult => {
  let appointment;
  let fieldsToUpdate = {};

  if (!primary) {
    appointment = { primary: newAppointment, secondary: [] };

    if (hasNearPro) {
      fieldsToUpdate = { appointmentNear: true };
    } else if (hasNearProToday) {
      fieldsToUpdate = { appointmentNear: false };
    }
  } else if (newAppointment.isSame(primary)) {
    appointment = { primary: null, secondary: [] };
    fieldsToUpdate = { appointmentNear: null };
  } else if (secondary.some((s) => s.isSame(newAppointment))) {
    appointment = {
      primary,
      secondary: secondary.filter((secondaryAppointment) => !secondaryAppointment.isSame(newAppointment)),
    };
  } else {
    appointment = {
      primary,
      secondary: [...secondary, newAppointment],
    };
  }

  return {
    appointment,
    fieldsToUpdate,
  };
};

export const getFirstPrestationFromContent = (basketContent: BasketContent) => {
  const category = basketContent[0];
  return category ? category.prestations[0] : undefined;
};

export const isChildcareRecurring = (basketContent: BasketContent) =>
  getFirstPrestationFromContent(basketContent) === PRESTATIONS.RECURRING_CHILDCARE;

export const hasPrestations = (basketContent: BasketContent) =>
  basketContent.some((category) => category.prestations.length > 0);

export const extractBasketContentFromBooking = (booking: Booking, universe: Universe): BasketContent =>
  compact(
    booking.prestations.map((prestationsForCategory) => {
      const category = universe.categories.find((cat) =>
        cat.subcategories.some((subcat) => subcat.prestations.some((p) => p.reference === prestationsForCategory[0])),
      );

      return category ? { reference: category.reference, prestations: prestationsForCategory } : undefined;
    }),
  );

export const extractAppointmentFromBooking = (booking: Booking, clearAppointments = false): Appointment =>
  clearAppointments
    ? {
        primary: null,
        secondary: [],
      }
    : {
        primary: moment(booking.appointment),
        secondary: (booking.other_disponibilities || []).map((d) => moment(d)),
      };

export const extractAppointmentDateFromBooking = (
  booking: Booking,
  { clearAppointments = false, shouldUpdateAllOccurrences = true } = {},
) => {
  // There is a special case for recurring orders when we want to update all occurences.
  // The booking is the first pending one and recurrence_orders is the list of future bookings.
  // There is a special case, when the current booking is currently done by the pro. In this case, the booking is not
  // in recurrence_orders because it is pending but not in the future.
  // For modification we want to only update bookings in the future not the current one so we initialize the appointment
  // date with the first recurrence_orders not with the booking appointment
  const isRecurringOrderAndUpdateAll = shouldUpdateAllOccurrences && shouldUseRecurrenceOrders(booking);
  return clearAppointments
    ? null
    : moment(isRecurringOrderAndUpdateAll ? booking.recurrence_orders![0].appointment : booking.appointment);
};

export const extractAddressFromBooking = (booking: Booking): Address => ({
  address: booking.address,
  address_details: booking.address_details,
  city: booking.city,
  lat: booking.lat,
  lng: booking.lng,
  street_name: booking.street_name,
  street_number: booking.street_number,
  zip_code: booking.zip_code,
  country_code: booking.country_code,
});

export const extractCustomerFromBooking = (booking: Booking) => ({
  first_name: booking.first_name,
  last_name: booking.last_name,
  phone: booking.phone,
  salutation: booking.salutation,
});

// We will need this utils as long as the structure of the front pro choices is different
// than the structure of the back pro choices (sent on the booking).
// The future goal is to harmonise the front and back structures, so that these different types
// and conversion utils are no longer necessary.
export const extractWantedProChoicesFromBooking = (potential: Potential): ProChoice[] =>
  (potential.pro_choices || [])
    .map(({ pro, choice }: ProChoiceAPI) => {
      // The back does not send us the source attribute
      // So we force it to 'previous_pro'
      // This is safe as long the transformed pro choices are not sent down to the back
      const transformedChoice: ProChoice = {
        id: pro.id,
        source: 'previous_pro',
        pro,
        value: choice,
      };

      return transformedChoice;
    })
    .filter(isWanted);

export const extractPromotionFromBooking = (booking: Booking): Promotion => ({
  // Properties that are frontend-only and not known to backend
  success: false,
  message: '',
  // Properties that come from the backend
  code: booking.promotion ? booking.promotion.code : '',
  amount: booking.promotion ? booking.promotion.amount : 0,
  calculation_type: booking.promotion ? booking.promotion.calculation_type : 'fixed',
});

export const extractGiftCardFromBooking = (
  booking: Pick<Booking, 'gift_code' | 'gift_value'>,
): GiftCard | undefined =>
  booking.gift_code
    ? {
        // If there is a gift code, we can trust the back to send us also the value
        code: booking.gift_code,
        value: booking.gift_value!,
        /**
         * TODO: We might need to extract other gift card props here in future
         * when the customer app supports gift cards fully.
         */
      }
    : undefined;

export const extractChildcarePrestationFromBooking = (booking: Booking) => {
  const { universe, prestations } = booking;
  const isChildcareUniverse = isChildcareReference(universe);

  return isChildcareUniverse ? prestations[0][0] : undefined;
};

const commonCreateBasketFromBooking = (
  booking: Booking,
  { clearAppointments = false, shouldUpdateAllOccurrences = true } = {},
): Partial<CustomerBasketState> => ({
  address: extractAddressFromBooking(booking),
  appointment: extractAppointmentFromBooking(booking, clearAppointments),
  appointmentDate: extractAppointmentDateFromBooking(booking, { clearAppointments, shouldUpdateAllOccurrences }),
  comments: booking.comments,
  duration: booking.duration,
  giftCard: extractGiftCardFromBooking(booking) || {},
  hasPets: booking.has_pets,
  isBookingForSelf: booking.booking_for_self,
  isInstantTaxCreditSelected: booking.instant_tax_credit_interested,
  proGender: booking.pro_gender,
  promotion: extractPromotionFromBooking(booking),
  weekly_recurrence: booking.weekly_recurrence,
  zone: booking.zone,
});

export const extractCleaningPrestationAndOptionsFromBooking = (booking: Booking) => {
  if (booking.prestations_with_options && booking.prestations_with_options.length > 0) {
    return {
      cleaningPrestation: booking.prestations_with_options[0].prestation,
      cleaningPrestationOptions: booking.prestations_with_options[0].options,
    };
  }
  return {};
};

export const createCleaningBasketFromBooking = (
  booking: Booking,
  { shouldUpdateAllOccurrences }: { shouldUpdateAllOccurrences: boolean },
) => ({
  ...extractCleaningPrestationAndOptionsFromBooking(booking),
  ...commonCreateBasketFromBooking(booking, { clearAppointments: false, shouldUpdateAllOccurrences }),
});

export const createBasketFromBooking = (
  booking: Booking,
  universe: Universe,
  { clearAppointments = false, shouldUpdateAllOccurrences = true } = {},
) => ({
  basketContent: extractBasketContentFromBooking(booking, universe),
  ...commonCreateBasketFromBooking(booking, { clearAppointments, shouldUpdateAllOccurrences }),
});

type WithComparisonReference = {
  referenceForComparison: string;
};

const addUniqIDToBasketContentCategory = (
  categories: BasketContentWithPrices,
): (BasketContentItemWithPrices & WithComparisonReference)[] => {
  const referenceCounts: Record<CategoryReference, number> = {};

  return categories.map((category) => {
    const { reference } = category;
    const referenceCount = referenceCounts[reference] || 0;
    referenceCounts[reference] = referenceCount + 1;

    return {
      ...category,
      referenceForComparison: `${reference}${referenceCount}`,
    };
  });
};

/**
 * A rough and unoptimized way to compare categories before and after the
 * modification of prestations inside a basket.
 *
 * TODO: Maybe we want to provide a more sophisticated diffing algorithm here.
 *
 * @param {*} previousCategories The categories before the modification
 * @param {*} categories The categories after modification
 */
export const getComparedBasketCategories = (
  previousCategories: BasketContentWithPrices,
  categories: BasketContentWithPrices,
) => {
  /**
   * The comparison is dependent on the order of the array and a single category
   * reference could appear several times inside the array (e.g. 'HOMME 1' and 'HOMME 2').
   */
  const alreadyCompared: BasketContentWithPrices = [];

  /**
   * We assume that the first previous category that matches the current category,
   * is a category that got modified. Therefore the matching is relying on the order
   * of each array. But only if we have not yet handled the category and if the
   * prestations really have changed for this category.
   */
  const comparedCategories = addUniqIDToBasketContentCategory(categories).map((category) => {
    const previousCategory = previousCategories.find(
      (cat) => cat.reference === category.reference && !alreadyCompared.includes(cat),
    );

    if (previousCategory) {
      alreadyCompared.push(previousCategory);

      if (!isEqual(previousCategory.prestations, category.prestations)) {
        return {
          ...category,
          prestationsPricesBeforeModification: previousCategory.prestationsPrices,
          priceBeforeModification: previousCategory.price,
        };
      }
    }

    return {
      ...category,
      priceBeforeModification: null,
      prestationsPricesBeforeModification: null,
    };
  });

  const removedCategories = addUniqIDToBasketContentCategory(previousCategories).reduce(
    (removedCategoriesArray: (BasketContentItemWithPrices & WithComparisonReference)[], previousCategory) => {
      const isCategoryStillPresent = comparedCategories.some(
        (category) => category.referenceForComparison === previousCategory.referenceForComparison,
      );

      if (!isCategoryStillPresent) {
        removedCategoriesArray.push({
          ...previousCategory,
          prestationsPrices: {},
          prestationsPricesBeforeModification: previousCategory.prestationsPrices,
          prestations: [],
          price: {
            standard: 0,
            reduced: 0,
          },
          priceBeforeModification: previousCategory.price,
        });
      }

      return removedCategoriesArray;
    },
    [],
  );

  return [...removedCategories, ...comparedCategories].map(({ referenceForComparison, ...attributes }) => attributes);
};

export const basketContentEqualsBookingContent = (
  basketContent: BasketContent,
  booking: Booking,
  universe: Universe,
) => {
  const bookingContent = extractBasketContentFromBooking(booking, universe);
  return isEqual(basketContent, bookingContent);
};

type IsCleaningBasketSameAsBookingParams = {
  booking: Booking;
  cleaningPrestation: CleaningPrestation;
  cleaningPrestationOptions?: CleaningPrestationOption[];
};

export const isCleaningPrestationAndOptionsSameAsBooking = ({
  booking,
  cleaningPrestation,
  cleaningPrestationOptions,
}: IsCleaningBasketSameAsBookingParams) => {
  const {
    cleaningPrestation: cleaningPrestationFromBooking,
    cleaningPrestationOptions: cleaningPrestationOptionsFromBooking,
  } = extractCleaningPrestationAndOptionsFromBooking(booking);

  if (!cleaningPrestationFromBooking || !cleaningPrestationOptionsFromBooking) return false;

  const optionReferencesFromBooking = cleaningPrestationOptionsFromBooking.map((option) => option.reference).sort();
  const optionReferences =
    cleaningPrestationOptions && cleaningPrestationOptions.map((option) => option.reference).sort();

  return (
    cleaningPrestationFromBooking.reference === cleaningPrestation.reference &&
    optionReferencesFromBooking.toString() === (optionReferences && optionReferences.toString())
  );
};

type HasModificationsForCleaningParams = {
  appointment: Appointment;
  booking?: Booking | null;
  cleaningPrestation: CleaningPrestation;
  cleaningPrestationOptions?: CleaningPrestationOption[];
  duration: number | null;
  weeklyRecurrence: number | null;
};

export const hasModificationsForCleaning = ({
  appointment,
  booking,
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  weeklyRecurrence,
}: HasModificationsForCleaningParams) => {
  if (!booking || !isCleaningReference(booking.universe)) {
    return false;
  }

  const hasPrestationsChanged = !isCleaningPrestationAndOptionsSameAsBooking({
    cleaningPrestation,
    cleaningPrestationOptions,
    booking,
  });

  const hasDurationChange = duration !== booking.duration;
  const hasRecurrenceChange = weeklyRecurrence !== booking.weekly_recurrence;
  // isSame(date, 'minute') checks everything from year to minute
  const hasPrimaryAppointmentChange = !moment(booking.appointment).isSame(appointment.primary, 'minute');

  const appointmentFromBooking = extractAppointmentFromBooking(booking);
  // orders secondaries are always equal to []
  const hasSecondaryAppointmentsChange = !areMomentArraysEqual(
    appointmentFromBooking.secondary,
    appointment.secondary,
  );

  return (
    hasPrestationsChanged ||
    hasDurationChange ||
    hasRecurrenceChange ||
    hasPrimaryAppointmentChange ||
    hasSecondaryAppointmentsChange
  );
};

export const getDurationPerWeekFromSchedules = (schedules: Schedule[] = []) =>
  schedules.reduce(
    (totalMinutes, { starts_at, ends_at }) => totalMinutes + parseTime(ends_at).diff(parseTime(starts_at), 'minutes'),
    0,
  );

export const priceOrDurationHasChanged = (
  price: number,
  duration: number,
  oldPrice?: number,
  oldDuration?: number | null,
) => !!oldPrice && !!oldDuration && (price !== oldPrice || duration !== oldDuration);

export const getCategoryTitleWithCount = (
  categoryIndex: number,
  categoryTitle: string,
  basketContent: BasketContent,
) => {
  const currentCatFilter = (cat: BasketContentItem) => cat.reference === basketContent[categoryIndex].reference;
  const currentCount =
    basketContent.filter(currentCatFilter).length > 1
      ? basketContent.slice(0, categoryIndex + 1).filter(currentCatFilter).length
      : undefined;
  return currentCount ? `${categoryTitle} ${currentCount}` : categoryTitle;
};

export const getAllBasketPrestations = (basketContent: BasketContent) =>
  flatten(basketContent.map((category) => category.prestations));

export const getPrestation = (categoryReference: string, prestationReference: string, universe: Universe) =>
  getAllPrestationsForCategory(categoryReference, universe).find((p) => p.reference === prestationReference);

export const getBasketPrestationsTitles = (
  categoryReference: string,
  universe: Universe,
  prestationRefs: string[],
  durationText?: string,
) => {
  const categoryPrestations = getAllPrestationsForCategory(categoryReference, universe);

  return getPrestationsTitles(
    prestationRefs.map((reference) => categoryPrestations.find((p) => p.reference === reference)),
    durationText,
  );
};

export const getBasketCategoryPrestationsList = (
  categoryReference: string,
  universe: Universe,
  prestationRefs: string[],
) => {
  const categoryPrestations = getAllPrestationsForCategory(categoryReference, universe);

  return prestationRefs.map((reference) => categoryPrestations.find((p) => p.reference === reference));
};

export const getCategoryCount = (basketContent: BasketContent, reference: CategoryReference) =>
  basketContent.filter((content) => content.reference === reference).length;

export const getTotalPrestationsCount = (basketContent: BasketContent) =>
  basketContent.reduce((sum, category) => sum + category.prestations.length, 0);

export const getBasketContentDuration = (basketContent: BasketContent, universe?: Universe) => {
  if (!universe) return 0;
  return basketContent.reduce((total, item) => {
    const prestations = getAllPrestationsForCategory(item.reference, universe);
    return (
      total +
      item.prestations.reduce(
        (sum, reference) =>
          sum + (prestations.find((prestation) => prestation.reference === reference)?.duration ?? 0),
        0,
      )
    );
  }, 0);
};

const areCategoriesEqual = (category: BasketContentItem, other: BasketContentItem) => {
  if (category.reference !== other.reference || category.prestations.length !== other.prestations.length) {
    return false;
  }

  return difference(category.prestations, other.prestations).length === 0;
};

/**
 * Returns the first category found that exists in only one the two baskets provided.
 *
 * @warning It only works if basket1 and basket2 are same length, otherwise
 * they are necessarily different and it makes no sense to call this function.
 */
const findDifferentCategory = (basket1: BasketContent, basket2: BasketContent): BasketContentItem | undefined => {
  if (basket1.length === 0 && basket2.length === 0) return undefined;

  const [firstItem, ...rest] = basket1;
  const sameCategoryIndex = basket2.findIndex((cat) => areCategoriesEqual(firstItem, cat));

  return sameCategoryIndex === -1
    ? firstItem
    : findDifferentCategory(
        rest,
        basket2.filter((_, i) => i !== sameCategoryIndex),
      );
};

export const hasBasketChanged = (basket: BasketContent, initialBasket: BasketContent) => {
  if (basket.length !== initialBasket.length) return true;

  return !!findDifferentCategory(initialBasket, basket);
};

export const hasGiftCard = (giftCard?: GiftCard | {}) => Boolean(giftCard && 'code' in giftCard && giftCard.code);

type ChildcareAllowanceAmountParams = {
  universe?: Universe;
  basketContent: BasketContent;
  childcareAllowanceParams: ChildcareAllowanceParams;
  childrenParams: ChildrenParams;
};

export const getChildcareAllowanceAmount = ({
  universe,
  basketContent,
  childcareAllowanceParams,
  childrenParams,
}: ChildcareAllowanceAmountParams) => {
  const isReccurent = isChildcareRecurring(basketContent);
  if (!universe || !isChildcare(universe) || !isReccurent) return 0;

  const { maritalStatus, incomeLevel } = childcareAllowanceParams;

  const levels = getChildcareAllowanceLevels({
    universe,
    maritalStatus,
    childrenCount: childrenParams.childrenCount,
    youngestChildAge: childrenParams.youngestChildAge,
  });

  if (!levels) return 0;

  const level = levels.find((l) => l.type === incomeLevel);
  return !level ? 0 : level.amount || 0;
};

const formatClosedTimeSlots = (closedTimeSlots: Moment[]) =>
  closedTimeSlots ? JSON.stringify(closedTimeSlots.map(formateDateForAPI)) : undefined;

const formatCleaningPrestationAndOptionsForApi = (
  cleaningPrestation: CleaningPrestation,
  cleaningPrestationOptions?: CleaningPrestationOption[],
) => {
  const prestationText = `0.${cleaningPrestation.reference}`;
  if (!cleaningPrestationOptions || cleaningPrestationOptions.length === 0) return prestationText;
  return `${prestationText},${cleaningPrestationOptions.map((option) => `0.${option.reference}`).join()}`;
};

export const buildPrestationGroups = ({
  basketContent,
  cleaningPrestation,
  cleaningPrestationOptions,
}: Pick<BasketState, 'basketContent' | 'cleaningPrestation' | 'cleaningPrestationOptions'>) => {
  if (cleaningPrestation)
    return formatCleaningPrestationAndOptionsForApi(cleaningPrestation, cleaningPrestationOptions);
  return formatBasketForAPI(basketContent);
};

export const buildBasketPrimaryAppointment = ({ appointment }: Pick<BasketState, 'appointment'>) =>
  appointment.primary ? formateDateForAPI(appointment.primary) : '';

export const buildBasketSecondaryAppointments = ({ appointment }: Pick<BasketState, 'appointment'>) =>
  appointment.secondary.map(formateDateForAPI).join(',');

type BuildBasketPotentialParams = {
  prices: BasketPrices | null;
  basket: BasketState;
  hasMembership?: boolean;
};

export const buildBasketPotential = ({
  prices,
  basket,
  hasMembership,
}: BuildBasketPotentialParams): GeneratedPotential => {
  const {
    universe,
    basketContent,
    duration,
    appointment,
    appointmentNear,
    customer,
    address,
    paymentInfos: { stripePaymentMethodId, stripePaymentIntentId, stripeSetupIntentId, preauthorizationSkipped },
    comments,
    proGender,
    hasPets,
    closedTimeSlots: { minAppointmentDelay, all: closedTimeSlots, fetchedAt },
    activities,
    childrenParams,
    childcareAllowanceParams,
    schedules,
    includeHolidays,
    promotion,
    giftCard,
    weekly_recurrence: weeklyRecurrence,
    zone,
    childcareHoursPerWeek,
  } = basket;
  const primaryAppointment = buildBasketPrimaryAppointment({ appointment });
  const otherDisponibilities = buildBasketSecondaryAppointments({ appointment });
  const withGiftCard = !promotion?.code && hasGiftCard(giftCard);

  // TODO replace any with a type specific for the parameter with send to the create potential api
  const potential: GeneratedPotential = {
    ...customer,
    ...address,
    // @ts-ignore
    universe,
    zone,
    comments,
    weekly_recurrence: weeklyRecurrence,
    prestation_groups: buildPrestationGroups(basket),
    pro_gender: proGender,
    appointment: primaryAppointment,
    other_disponibilities: otherDisponibilities,
    appointment_near: appointmentNear,
    stripe_payment_method_id: stripePaymentMethodId,
    stripe_payment_intent_id: stripePaymentIntentId,
    stripe_setup_intent_id: stripeSetupIntentId,
    preauthorization_skipped: preauthorizationSkipped,
    remember_credit_card: true,
    closed_time_slots: formatClosedTimeSlots(closedTimeSlots),
    promo_code: promotion.success ? promotion.code : '',
    has_pets: hasPets,
    min_appointment_delay: minAppointmentDelay,
    zip_code_submitted_at: fetchedAt ? formateDateForAPI(fetchedAt) : undefined,
    // Address needs to be set when this method is called
    country_code: address!.country_code,
  };

  if (prices) {
    const hasMembershipPrices = hasMembership && (prices.kind === 'wellness' || prices.kind === 'cleaning');
    const totalPrice = hasMembershipPrices ? prices.membership.totalPrice : prices.totalPrice;
    const promoValue = hasMembershipPrices ? prices.membership.promoValue : prices.promoValue;
    const giftValue = hasMembershipPrices ? prices.membership.giftValue : prices.giftValue;

    potential.promo_value = withGiftCard ? giftValue : promoValue;
    potential.gift_code = withGiftCard ? (giftCard as GiftCard).code : '';
    // We ensure the total price (in cents) is always an integer.
    potential.total_price = Math.round(totalPrice);
  }

  /**
   * Only for cleaning the frontend needs to calculate the duration and communicate it
   * to the server. For all other universes we avoid passing the duration as it does
   * not get properely re-calculated by us and if we sent a wrong duration value,
   * the server could complain.
   */
  if (isHourlyBasedReference(universe)) {
    (potential as ChildcareGeneratedPotential | CleaningGeneratedPotential).duration = duration;
  }

  if (isChildcareReference(universe)) {
    (potential as ChildcareGeneratedPotential).children_count = childrenParams.childrenCount;
    (potential as ChildcareGeneratedPotential).youngest_child_age = childrenParams.youngestChildAge;
    (potential as ChildcareGeneratedPotential).youngest_child_born_on = childrenParams.youngestBirthdate;
    (potential as ChildcareGeneratedPotential).activities = activities;
    // Backend wants all data or nothing : incomeLevel is the last data we get in allowance screen
    if (childcareAllowanceParams.incomeLevel) {
      (potential as ChildcareGeneratedPotential).marital_status = childcareAllowanceParams.maritalStatus;
      (potential as ChildcareGeneratedPotential).income_level = childcareAllowanceParams.incomeLevel;
    }
    (potential as ChildcareGeneratedPotential).schedules = schedules;
    (potential as ChildcareGeneratedPotential).include_holidays = includeHolidays;

    // This is a temporary solution demanded by the backend and could probably change in future
    if (isChildcareRecurring(basketContent) && prices && prices.kind === 'childcare') {
      (potential as RecurringChildcareGeneratedPotential).estimated_weekly_hours = childcareHoursPerWeek;
      (potential as RecurringChildcareGeneratedPotential).duration = childcareHoursPerWeek
        ? childcareHoursPerWeek * 60
        : getDurationPerWeekFromSchedules(schedules);
      // We ensure the total price (in cents) is always an integer.
      (potential as RecurringChildcareGeneratedPotential).total_price = Math.round(prices.weeklyTotalPrice);
    }
  }

  return potential;
};
